import { Host, h } from "@stencil/core";
import { analyzeLabelExistence, getElementInternalsLabel } from "../../common/analysis/accessibility";
import { getControlRegisterProperty } from "../../common/registry-properties";
import { EDIT_HOST_PARTS, EDIT_PARTS_DICTIONARY, SCROLLABLE_CLASS } from "../../common/reserved-names";
import { adoptCommonThemes } from "../../common/theme";
import { tokenMap, updateDirectionInImageCustomVar } from "../../common/utils";
let GET_IMAGE_PATH_CALLBACK_REGISTRY;
const DEFAULT_GET_IMAGE_PATH_CALLBACK = imageSrc => ({ base: imageSrc });
const AUTOFILL_START_ANIMATION_NAME = "AutoFillStart";
const DATE_TYPES = ["datetime-local", "date", "time"];
const MAX_DATE_VALUE = {
    date: "9999-12-31",
    "datetime-local": "9999-12-31T23:59:59"
};
const MIN_DATE_VALUE = {
    date: "0001-01-01",
    "datetime-local": "0001-01-01T00:00:00"
};
const TEXTAREA_FLOATING_CLASSES = `content autofill multiline-floating ${SCROLLABLE_CLASS}`;
const TEXTAREA_INLINE_CLASSES = `content autofill multiline-inline ${SCROLLABLE_CLASS}`;
/**
 * A wrapper for the input and textarea elements. It additionally provides:
 *  - A placeholder for `"date"`, `"datetime-local"` and `"time"` types.
 *  - An action button.
 *  - Useful style resets.
 *  - Support for picture formatting.
 *  - Support to auto grow the control when used with multiline (useful to
 *    model chat inputs).
 *  - An image which can have multiple states.
 *  - Support for debouncing the input event.
 *
 * @part date-placeholder - A placeholder displayed when the control is editable (`readonly="false"`), has no value set, and its type is `"datetime-local" | "date" | "time"`.
 *
 * @slot additional-content-before - The slot used for the additional content when `showAdditionalContentBefore === true`.
 * @slot additional-content-after - The slot used for the additional content when `showAdditionalContentAfter === true`.
 */
export class ChEdit {
    constructor() {
        this.inputModeEditorDisplayed = false;
        this.isFocusOnControl = false;
        this.pictureValue = undefined;
        this.autoFilled = false;
        this.accessibleName = undefined;
        this.autocapitalize = undefined;
        this.autocomplete = "off";
        this.autoFocus = false;
        this.autoGrow = false;
        this.debounce = 0;
        this.disabled = false;
        this.getImagePathCallback = undefined;
        this.hostParts = undefined;
        this.maxLength = undefined;
        this.mode = undefined;
        this.multiline = false;
        this.name = undefined;
        this.pattern = undefined;
        this.picture = undefined;
        this.pictureCallback = undefined;
        this.placeholder = undefined;
        this.preventEnterInInputEditorMode = false;
        this.readonly = false;
        this.showAdditionalContentAfter = false;
        this.showAdditionalContentBefore = false;
        this.showPassword = false;
        this.showPasswordButton = false;
        this.spellcheck = false;
        this.startImgSrc = undefined;
        this.startImgType = "background";
        this.translations = {
            accessibleName: {
                clearSearchButton: "Clear search",
                hidePasswordButton: "Hide password",
                showPasswordButton: "Show password"
            }
        };
        this.type = "text";
        this.value = undefined;
    }
    #accessibleNameFromExternalLabel;
    #startImage;
    #debounceId;
    #shouldComputePictureValue = false;
    // Refs
    // TODO: StencilJS issue. We have to use two refs because StencilJS does not,
    // update the ref when updating the multiline property or the DOM element is
    // moved
    #inputRef;
    #textareaRef;
    #textareaInsideContainerRef;
    internals;
    el;
    getImagePathCallbackChanged() {
        this.#computeImage();
    }
    pictureChanged() {
        this.#shouldComputePictureValue = true;
    }
    pictureCallbackChanged() {
        this.#shouldComputePictureValue = true;
    }
    startImgSrcChanged() {
        this.#computeImage();
    }
    valueChanged(newValue) {
        this.#shouldComputePictureValue = true;
        if (!this.#getInputRef()) {
            return;
        }
        /**
         * Synchronize the input value with value prop. This use case is only
         * needed when the value prop is changed outside of the component.
         * Without this verification, the following case would occur:
         *  - ValueChanging. Input ref: "X"
         *  - Render. Value prop: "X"
         *  - (Enter key event resets the value). Value prop: "" <---- The Angular's UIModel now has value = ""
         *  - ChangeEvent. Input ref: "X"
         *  Result: Angular's UIModel has value = "", but the control has value = "X"
         */
        if (this.#getInputRef().value !== this.value) {
            this.#getInputRef().value = this.value;
        }
        // Update form value
        this.internals.setFormValue(newValue);
    }
    /**
     * The `change` event is emitted when a change to the element's value is
     * committed by the user. Unlike the `input` event, the `change` event is not
     * necessarily fired for each change to an element's value but when the
     * control loses focus.
     * This event is _NOT_ debounced by the `debounce` property.
     */
    change;
    /**
     * Fired synchronously when the value is changed.
     * This event is debounced by the `debounce` property.
     */
    input;
    /**
     * Fired when the visibility of the password (when using `type="password"`)
     * is changed by clicking on the show password button.
     *
     * The detail contains the new value of the `showPassword` property.
     */
    passwordVisibilityChange;
    #getInputRef = () => this.#inputRef ?? this.#textareaRef ?? this.#textareaInsideContainerRef;
    #computeImage = () => {
        if (!this.startImgSrc) {
            this.#startImage = null;
            return;
        }
        const getImagePathCallback = this.getImagePathCallback ?? GET_IMAGE_PATH_CALLBACK_REGISTRY;
        if (!getImagePathCallback) {
            this.#startImage = null;
            return;
        }
        const img = getImagePathCallback(this.startImgSrc);
        if (!img) {
            this.#startImage = null;
            return;
        }
        this.#startImage = updateDirectionInImageCustomVar(img, "start");
    };
    #getValueFromEvent = (event) => event.target.value;
    #setValueAndEmitInputEventWithDebounce = (valueToEmit) => {
        clearTimeout(this.#debounceId);
        if (this.debounce > 0) {
            this.#debounceId = setTimeout(() => {
                this.value = valueToEmit;
                this.input.emit(valueToEmit);
            }, this.debounce);
        }
        else {
            this.value = valueToEmit;
            this.input.emit(valueToEmit);
        }
    };
    #handleAutoFill = (event) => {
        this.autoFilled = event.animationName === AUTOFILL_START_ANIMATION_NAME;
    };
    #handleChange = (event) => {
        this.value = this.#getValueFromEvent(event);
        this.change.emit(event);
    };
    #handleValueChanging = (event) => {
        event.stopPropagation();
        // Don't allow invalid values
        if (!this.#getInputRef().validity.valid) {
            this.#getInputRef().value = this.value;
            return;
        }
        this.#setValueAndEmitInputEventWithDebounce(this.#getValueFromEvent(event));
    };
    #clearValue = (event) => {
        event.stopPropagation();
        this.#setValueAndEmitInputEventWithDebounce("");
        requestAnimationFrame(() => this.el.focus());
    };
    #togglePasswordVisibility = (event) => {
        event.stopPropagation();
        const newShowPassword = !this.showPassword;
        this.showPassword = newShowPassword;
        this.passwordVisibilityChange.emit(newShowPassword);
    };
    #hasAdditionalContent = () => this.showAdditionalContentBefore || this.showAdditionalContentAfter;
    // - - - - - - - - - - - - - - - - - - - - - -
    //           Input Mode Editor (IME)
    // - - - - - - - - - - - - - - - - - - - - - -
    #inputModeEditorInProgress = (event) => {
        event.stopPropagation();
        this.inputModeEditorDisplayed = true;
    };
    #inputModeEditorEnded = (event) => {
        event.stopPropagation();
        this.inputModeEditorDisplayed = false;
    };
    #preventEnterOnKeyDown = (event) => {
        // Prevent the default action of the Enter key when in input editor mode
        if (event.key === "Enter") {
            // Stop any remaining keydown listeners from being called
            event.stopPropagation();
            // Stop the form submit
            event.preventDefault();
        }
    };
    // - - - - - - - - - - - - - - - - - - - - - -
    //                  Pictures
    // - - - - - - - - - - - - - - - - - - - - - -
    #hasPictureApplied = () => this.picture && !!this.pictureCallback;
    #computePictureValue(value) {
        if (this.#hasPictureApplied()) {
            this.pictureValue = this.pictureCallback(value, this.picture).trim();
        }
    }
    #showPictureOnFocus = () => {
        this.isFocusOnControl = true;
    };
    #removePictureOnBlur = () => {
        this.isFocusOnControl = false;
    };
    #renderTextarea = (canAddListeners, keyDownListener) => (h("textarea", { autoFocus: this.autoFocus, "aria-label": this.#accessibleNameFromExternalLabel || this.accessibleName || null, autoCapitalize: this.autocapitalize, autoComplete: this.autocomplete, class: this.#hasAdditionalContent() && !this.autoGrow
            ? TEXTAREA_INLINE_CLASSES
            : TEXTAREA_FLOATING_CLASSES, disabled: this.disabled, maxLength: this.maxLength, placeholder: this.placeholder, readOnly: this.readonly, spellcheck: this.spellcheck, value: this.value,
        // Event listeners
        onChange: canAddListeners && this.#handleChange, onInput: canAddListeners && this.#handleValueChanging, onAnimationStart: canAddListeners && this.#handleAutoFill, onKeyDown: keyDownListener, ref: el => 
        // This is a WA due to a StencilJS bug not refreshing the ref when the
        // element is moved
        this.autoGrow && this.#hasAdditionalContent()
            ? (this.#textareaInsideContainerRef = el)
            : (this.#textareaRef = el) }));
    #renderTextareaWithAdditionalContent = (canAddListeners, keyDownListener) => {
        // Floating as a "normal textarea"
        if (!this.#hasAdditionalContent()) {
            return [
                this.#renderTextarea(canAddListeners, keyDownListener),
                this.autoGrow && (h("div", { "aria-hidden": "true", class: "hidden-multiline" }, this.value))
            ];
        }
        // Inline case
        if (!this.autoGrow) {
            return this.#renderTextarea(canAddListeners, keyDownListener);
        }
        // Floating inside a container to implement the Auto Grow
        return (h("div", { class: "multiline-container" }, this.#renderTextarea(canAddListeners, keyDownListener), h("div", { "aria-hidden": "true", class: "hidden-multiline" }, this.value)));
    };
    connectedCallback() {
        adoptCommonThemes(this.el.shadowRoot.adoptedStyleSheets);
        // Initialize default getImagePathCallback
        GET_IMAGE_PATH_CALLBACK_REGISTRY ??=
            getControlRegisterProperty("getImagePathCallback", "ch-edit") ??
                DEFAULT_GET_IMAGE_PATH_CALLBACK;
        this.#computeImage();
        this.#computePictureValue(this.value);
        // Accessibility
        this.internals.setFormValue(this.value);
        const labels = this.internals.labels;
        this.#accessibleNameFromExternalLabel = getElementInternalsLabel(labels);
        // Report any accessibility issue
        analyzeLabelExistence(this.el, "ch-edit", labels, this.#accessibleNameFromExternalLabel, this.accessibleName);
    }
    componentWillUpdate() {
        if (this.#shouldComputePictureValue) {
            this.#shouldComputePictureValue = false;
            this.#computePictureValue(this.value);
        }
    }
    componentDidLoad() {
        if (this.autoFocus) {
            this.el.focus();
        }
    }
    // TODO: Remove the icon with multiline and add overflow: clip in the Host with multiline
    render() {
        const { accessibleName } = this.translations;
        const isDateType = DATE_TYPES.includes(this.type);
        const showDatePlaceholder = isDateType && this.placeholder && !this.value;
        const shouldDisplayPicture = this.#hasPictureApplied();
        const canAddListeners = !this.disabled && !this.readonly;
        const renderClearButton = !this.multiline && this.type === "search" && !!this.value;
        const renderShowPasswordButton = this.showPasswordButton && !this.multiline && this.type === "password";
        const compositionStartListener = canAddListeners &&
            this.preventEnterInInputEditorMode &&
            !this.inputModeEditorDisplayed &&
            this.#inputModeEditorInProgress;
        // TODO: What happens when the input/textarea losses focus or it disabled
        // in runtime when the IME is displayed?
        // Does the compositionend event fire?
        const compositionEndListener = canAddListeners &&
            this.preventEnterInInputEditorMode &&
            this.inputModeEditorDisplayed &&
            this.#inputModeEditorEnded;
        const keyDownListener = canAddListeners &&
            this.preventEnterInInputEditorMode &&
            this.inputModeEditorDisplayed &&
            this.#preventEnterOnKeyDown;
        return (h(Host, { key: '5301a430dbfcab204b27c94b44b1190d17855e89', class: {
                "ch-edit--auto-fill": this.autoFilled,
                "ch-edit--cursor-text": !isDateType && !this.disabled,
                "ch-edit--editable-date": isDateType && !this.readonly,
                "ch-edit--multiline": this.multiline && this.autoGrow,
                "ch-edit--clear-button": renderClearButton,
                "ch-edit--show-password-button": renderShowPasswordButton,
                [`ch-edit-start-img-type--${this.startImgType} ch-edit-pseudo-img--start`]: !this.multiline && !!this.#startImage
            },
            // TODO: Add unit tests for this feature, since it breaks custom parts
            // rendered outside of the ch-edit render() method
            part: tokenMap({
                [EDIT_HOST_PARTS.EMPTY_VALUE]: !this.value,
                [this.hostParts]: !!this.hostParts
            }), style: !this.multiline ? this.#startImage?.styles ?? undefined : null, "data-text-align": "", "data-valign": !this.multiline ? "" : undefined,
            // Listeners for detecting Input Editor Mode (IME)
            onCompositionstart: compositionStartListener, onCompositionend: compositionEndListener }, this.showAdditionalContentBefore && (h("slot", { key: '075e3a9b02f277384b99eafb7da6c4627570cc24', name: "additional-content-before" })), this.multiline
            ? this.#renderTextareaWithAdditionalContent(canAddListeners, keyDownListener)
            : [
                h("input", { autoFocus: this.autoFocus, "aria-label": this.#accessibleNameFromExternalLabel ||
                        this.accessibleName ||
                        null, autoCapitalize: this.autocapitalize, autoComplete: this.autocomplete, class: {
                        "content input autofill": true,
                        "null-date": showDatePlaceholder
                    }, disabled: this.disabled, inputMode: this.mode, maxLength: this.maxLength, max: MAX_DATE_VALUE[this.type], min: MIN_DATE_VALUE[this.type], pattern: this.pattern || undefined, placeholder: this.placeholder, readOnly: this.readonly, spellcheck: this.spellcheck, step: isDateType ? "1" : undefined, type: this.type === "password" && this.showPassword
                        ? "text"
                        : this.type, value: shouldDisplayPicture && !this.isFocusOnControl
                        ? this.pictureValue
                        : this.value,
                    // Event listeners
                    onAnimationStart: canAddListeners && this.#handleAutoFill, onChange: canAddListeners && this.#handleChange, onInput: canAddListeners && this.#handleValueChanging, onKeyDown: keyDownListener, onFocus: canAddListeners &&
                        shouldDisplayPicture &&
                        !this.isFocusOnControl &&
                        this.#showPictureOnFocus, onBlur: canAddListeners &&
                        shouldDisplayPicture &&
                        this.isFocusOnControl &&
                        this.#removePictureOnBlur, ref: el => (this.#inputRef = el) }),
                // Implements a non-native placeholder for date types. TODO: Add unit tests for this
                showDatePlaceholder && (h("div", { "aria-hidden": "true", class: "date-placeholder", part: EDIT_PARTS_DICTIONARY.DATE_PLACEHOLDER }, this.placeholder))
            ], this.showAdditionalContentAfter && (h("slot", { key: '37c83c986e2e1558647441c687e526bc41cc8fb4', name: "additional-content-after" })), renderClearButton && (h("button", { key: '0ecec6984e2191929e1de741b41f70266b5a60a9', "aria-label": accessibleName.clearSearchButton, class: "clear-button", part: tokenMap({
                [EDIT_PARTS_DICTIONARY.CLEAR_BUTTON]: true,
                [EDIT_PARTS_DICTIONARY.DISABLED]: this.disabled
            }), type: "button", onClick: !this.disabled && this.#clearValue })), renderShowPasswordButton && (h("button", { key: '447f353f8d651d38a870c773f1bb60f7c9885872', "aria-label": this.showPassword
                ? accessibleName.hidePasswordButton
                : accessibleName.showPasswordButton, class: {
                "show-password-button": true,
                "show-password-button--hidden": !this.showPassword
            }, part: tokenMap({
                [EDIT_PARTS_DICTIONARY.SHOW_PASSWORD]: true,
                [EDIT_PARTS_DICTIONARY.DISABLED]: this.disabled,
                [EDIT_PARTS_DICTIONARY.PASSWORD_DISPLAYED]: this.showPassword,
                [EDIT_PARTS_DICTIONARY.PASSWORD_HIDDEN]: !this.showPassword
            }), type: "button", onClick: !this.disabled && this.#togglePasswordVisibility }))));
    }
    static get is() { return "ch-edit"; }
    static get encapsulation() { return "shadow"; }
    static get delegatesFocus() { return true; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["edit.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["edit.css"]
        };
    }
    static get properties() {
        return {
            "accessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string, typically 1 to 3 words, that authors associate\nwith an element to provide users of assistive technologies with a label\nfor the element."
                },
                "attribute": "accessible-name",
                "reflect": false
            },
            "autocapitalize": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the auto-capitalization behavior. Same as [autocapitalize](https://developer.apple.com/library/content/documentation/AppleApplications/Reference/SafariHTMLRef/Articles/Attributes.html#//apple_ref/doc/uid/TP40008058-autocapitalize)\nattribute for `input` elements. Only supported by Safari and Chrome."
                },
                "attribute": "autocapitalize",
                "reflect": false
            },
            "autocomplete": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "| \"on\"\n    | \"off\"\n    | \"current-password\"\n    | \"new-password\"",
                    "resolved": "\"current-password\" | \"new-password\" | \"off\" | \"on\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute indicates whether the value of the control can be\nautomatically completed by the browser. Same as [autocomplete](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-autocomplete)\nattribute for `input` elements."
                },
                "attribute": "autocomplete",
                "reflect": false,
                "defaultValue": "\"off\""
            },
            "autoFocus": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the control automatically get focus when the page loads."
                },
                "attribute": "auto-focus",
                "reflect": false,
                "defaultValue": "false"
            },
            "autoGrow": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property defines if the control size will grow automatically, to\nadjust to its content size."
                },
                "attribute": "auto-grow",
                "reflect": false,
                "defaultValue": "false"
            },
            "debounce": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a debounce for the input event."
                },
                "attribute": "debounce",
                "reflect": false,
                "defaultValue": "0"
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the element is disabled.\nIf disabled, it will not fire any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": true,
                "defaultValue": "false"
            },
            "getImagePathCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    imageSrc: string\n  ) => GxImageMultiState | undefined",
                    "resolved": "(imageSrc: string) => GxImageMultiState",
                    "references": {
                        "GxImageMultiState": {
                            "location": "import",
                            "path": "../../common/types",
                            "id": "src/common/types.ts::GxImageMultiState"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies a callback that is executed when the path for an\nstartImgSrc needs to be resolved."
                }
            },
            "hostParts": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a set of parts to use in the Host element (`ch-edit`)."
                },
                "attribute": "host-parts",
                "reflect": false
            },
            "maxLength": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property defines the maximum string length that the user can enter\ninto the control."
                },
                "attribute": "max-length",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "mode": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "EditInputMode | undefined",
                    "resolved": "\"decimal\" | \"email\" | \"none\" | \"numeric\" | \"search\" | \"tel\" | \"text\" | \"url\"",
                    "references": {
                        "EditInputMode": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/edit/types.ts::EditInputMode"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute hints at the type of data that might be entered by the user\nwhile editing the element or its contents. This allows a browser to\ndisplay an appropriate virtual keyboard. Only works when\n`multiline === false`."
                },
                "attribute": "mode",
                "reflect": false
            },
            "multiline": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Controls if the element accepts multiline text."
                },
                "attribute": "multiline",
                "reflect": false,
                "defaultValue": "false"
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies the `name` of the control when used in a form."
                },
                "attribute": "name",
                "reflect": true
            },
            "pattern": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute specifies a regular expression the form control's value\nshould match. Only works when `multiline === false`."
                },
                "attribute": "pattern",
                "reflect": false,
                "defaultValue": "undefined"
            },
            "picture": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a picture to apply for the value of the control. Only works if\nnot `multiline`."
                },
                "attribute": "picture",
                "reflect": false
            },
            "pictureCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(value: any, picture: string) => string",
                    "resolved": "(value: any, picture: string) => string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the callback to execute when the picture must computed for the\nnew value."
                }
            },
            "placeholder": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A hint to the user of what can be entered in the control. Same as [placeholder](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-placeholder)\nattribute for `input` elements."
                },
                "attribute": "placeholder",
                "reflect": false
            },
            "preventEnterInInputEditorMode": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies whether the ch-edit should prevent the default behavior of the\n`Enter` key when in input editor mode.\n\nIn other words, if `true`, pressing `Enter` will not submit the form or\ntrigger the default action of the `Enter` key in an input field when the\nuser-edit is in input editor mode."
                },
                "attribute": "prevent-enter-in-input-editor-mode",
                "reflect": false,
                "defaultValue": "false"
            },
            "readonly": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute indicates that the user cannot modify the value of the control.\nSame as [readonly](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attr-readonly)\nattribute for `input` elements."
                },
                "attribute": "readonly",
                "reflect": false,
                "defaultValue": "false"
            },
            "showAdditionalContentAfter": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If `true`, a slot is rendered in the edit with `\"additional-content-after\"`\nname.\nThis slot is intended to customize the internal content of the edit by\nadding additional elements after the edit content."
                },
                "attribute": "show-additional-content-after",
                "reflect": false,
                "defaultValue": "false"
            },
            "showAdditionalContentBefore": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If `true`, a slot is rendered in the edit with `\"additional-content-before\"`\nname.\nThis slot is intended to customize the internal content of the edit by\nadding additional elements before the edit content."
                },
                "attribute": "show-additional-content-before",
                "reflect": false,
                "defaultValue": "false"
            },
            "showPassword": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the password is displayed as plain text when using\n`type = \"password\"`."
                },
                "attribute": "show-password",
                "reflect": false,
                "defaultValue": "false"
            },
            "showPasswordButton": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if the show password button is displayed when using\n`type = \"password\"`."
                },
                "attribute": "show-password-button",
                "reflect": false,
                "defaultValue": "false"
            },
            "spellcheck": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies whether the element may be checked for spelling errors"
                },
                "attribute": "spellcheck",
                "reflect": false,
                "defaultValue": "false"
            },
            "startImgSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the source of the start image."
                },
                "attribute": "start-img-src",
                "reflect": false
            },
            "startImgType": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Exclude<ImageRender, \"img\">",
                    "resolved": "\"background\" | \"mask\"",
                    "references": {
                        "Exclude": {
                            "location": "global",
                            "id": "global::Exclude"
                        },
                        "ImageRender": {
                            "location": "import",
                            "path": "../../common/types",
                            "id": "src/common/types.ts::ImageRender"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the source of the start image."
                },
                "attribute": "start-img-type",
                "reflect": false,
                "defaultValue": "\"background\""
            },
            "translations": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "EditTranslations",
                    "resolved": "{ accessibleName: { clearSearchButton?: \"Clear search\"; hidePasswordButton?: \"Hide password\"; showPasswordButton?: \"Show password\"; }; }",
                    "references": {
                        "EditTranslations": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/edit/types.ts::EditTranslations"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the literals required in the control."
                },
                "defaultValue": "{\n    accessibleName: {\n      clearSearchButton: \"Clear search\",\n      hidePasswordButton: \"Hide password\",\n      showPasswordButton: \"Show password\"\n    }\n  }"
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "EditType",
                    "resolved": "\"date\" | \"datetime-local\" | \"email\" | \"file\" | \"number\" | \"password\" | \"search\" | \"tel\" | \"text\" | \"time\" | \"url\"",
                    "references": {
                        "EditType": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/edit/types.ts::EditType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The type of control to render. A subset of the types supported by the `input` element is supported:\n\n* `\"date\"`\n* `\"datetime-local\"`\n* `\"email\"`\n* `\"file\"`\n* `\"number\"`\n* `\"password\"`\n* `\"search\"`\n* `\"tel\"`\n* `\"text\"`\n* `\"url\"`"
                },
                "attribute": "type",
                "reflect": false,
                "defaultValue": "\"text\""
            },
            "value": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The initial value of the control."
                },
                "attribute": "value",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "inputModeEditorDisplayed": {},
            "isFocusOnControl": {},
            "pictureValue": {},
            "autoFilled": {}
        };
    }
    static get events() {
        return [{
                "method": "change",
                "name": "change",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "The `change` event is emitted when a change to the element's value is\ncommitted by the user. Unlike the `input` event, the `change` event is not\nnecessarily fired for each change to an element's value but when the\ncontrol loses focus.\nThis event is _NOT_ debounced by the `debounce` property."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "input",
                "name": "input",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired synchronously when the value is changed.\nThis event is debounced by the `debounce` property."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }, {
                "method": "passwordVisibilityChange",
                "name": "passwordVisibilityChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the visibility of the password (when using `type=\"password\"`)\nis changed by clicking on the show password button.\n\nThe detail contains the new value of the `showPassword` property."
                },
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "getImagePathCallback",
                "methodName": "getImagePathCallbackChanged"
            }, {
                "propName": "picture",
                "methodName": "pictureChanged"
            }, {
                "propName": "pictureCallback",
                "methodName": "pictureCallbackChanged"
            }, {
                "propName": "startImgSrc",
                "methodName": "startImgSrcChanged"
            }, {
                "propName": "value",
                "methodName": "valueChanged"
            }];
    }
    static get attachInternalsMemberName() { return "internals"; }
}
